import React from 'react';
import { X } from 'lucide-react';

export interface MeetingFilter {
  community: string | null;
  year: string | null;
  dateFrom: string | null;
  dateTo: string | null;
}

interface MeetingFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: MeetingFilter;
  onFilterChange: (filters: Partial<MeetingFilter>) => void;
  communities: string[];
  years: string[];
}

export default function MeetingFilterPanel({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  communities,
  years
}: MeetingFilterPanelProps) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      {/* Panel */}
      <div className="fixed inset-x-0 bottom-0 sm:inset-auto sm:absolute sm:right-0 sm:top-full sm:mt-2 z-50">
        <div className="w-full sm:w-96 bg-white rounded-t-lg sm:rounded-lg shadow-lg border border-gray-200">
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button 
                onClick={onClose} 
                className="p-2 -mr-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Community
                </label>
                <select
                  value={filters.community || ''}
                  onChange={(e) => onFilterChange({ community: e.target.value || null })}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">All Communities</option>
                  {communities.map((community) => (
                    <option key={community} value={community}>
                      {community}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year
                </label>
                <select
                  value={filters.year || ''}
                  onChange={(e) => onFilterChange({ year: e.target.value || null })}
                  className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">All Years</option>
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={filters.dateFrom || ''}
                    onChange={(e) => onFilterChange({ dateFrom: e.target.value || null })}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={filters.dateTo || ''}
                    onChange={(e) => onFilterChange({ dateTo: e.target.value || null })}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="To"
                  />
                </div>
              </div>

              <div className="pt-4 border-t">
                <button
                  onClick={() => onFilterChange({
                    community: null,
                    year: null,
                    dateFrom: null,
                    dateTo: null
                  })}
                  className="w-full px-4 py-2 text-sm text-primary hover:bg-primary/5 rounded-md"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
